import type { CaptureResult, PostHog } from 'posthog-js';

import { isChunkLoadError } from './chunkReload';

/**
 * PostHog: the onboarding funnel, session replay of the journey a brand-new
 * user walks (signup → the welcome fork → creating and setting up a league),
 * and the client-side error tracking utils/errorReporting.ts reports into.
 *
 * The project key travels the same channel as every other client-published
 * secret — appConfig → ViewService.getConfigForTemplate → window.viewConfig —
 * and no key means no integration at all: nothing is imported, no request is
 * made and no message is printed. That is the state dev, test and Playwright
 * run in, and the state production runs in until the key is provisioned.
 *
 * GA4 (see utils/ga) stays the product-wide event stream. PostHog is here for
 * the funnel and the replay attached to it, so the two report side by side
 * rather than one replacing the other.
 */

/** Every event this app emits. A union rather than a string so a typo is a build error, not a metric that silently never arrives. */
export type AnalyticsEvent =
  /** A signup request succeeded, fired before the navigation into the app. */
  | 'signup_completed'
  /** A step of the post-signup onboarding was rendered. */
  | 'onboarding_step_viewed'
  /** The user acted on that step (chose a role, submitted or skipped the survey). */
  | 'onboarding_step_answered'
  /** A league setup checklist step went from incomplete to complete. */
  | 'league_setup_step_completed'
  /** The whole checklist completed. */
  | 'league_setup_completed'
  /** An API call came back with a client error the app asked for and already handles — a refused input, a lost session, a refusal, a not-found the caller renders. Counted here so the outcome stays answerable without opening an error tracking issue (utils/errorReporting.ts). */
  | 'api_client_error';

/** Deliberately narrow: identifiers and step names only. Nothing a person typed, and nothing that names them. */
export type AnalyticsProperties = Record<string, string | number | boolean>;

/** Replay follows the account for its first two weeks — long enough to cover setting a league up over a few sittings, short enough that routine use is never recorded. */
export const NEW_ACCOUNT_REPLAY_DAYS = 14;

const DAY_MS = 24 * 60 * 60 * 1000;

const DEFAULT_HOST = 'https://us.i.posthog.com';

export interface AnalyticsGateInput {
  /** A project key reached the browser. */
  hasKey: boolean;
  /** Replay every visitor instead of only the new-user journey; server-controlled (POSTHOG_REPLAY_ALL) so it can be narrowed without a deploy once volume grows. */
  replayAll: boolean;
  /** The signed-in user, or null for a visitor partway through the signup journey. */
  loggedUser: { id: number | string } | null;
  /** That user's account creation date; null when the layout shipped none. */
  userCreatedAt: Date | null;
  now: Date;
}

export interface AnalyticsGate {
  /** Load and start the SDK at all. */
  init: boolean;
  /** Record a session replay on top of the events. */
  record: boolean;
}

/**
 * Who gets recorded, as a pure decision so the rule can be read and tested
 * without a browser.
 *
 * Replay is the invasive half of this integration, so it is granted rather than
 * withheld: an unauthenticated visitor is by definition somewhere in the signup
 * journey this exists to watch, and a fresh account is still walking the setup
 * that follows it. Everyone else — the established leagues going about their
 * season — contributes events and nothing more.
 */
export function resolveAnalyticsGate({ hasKey, replayAll, loggedUser, userCreatedAt, now }: AnalyticsGateInput): AnalyticsGate {
  if (!hasKey) return { init: false, record: false };
  if (replayAll) return { init: true, record: true };
  if (!loggedUser) return { init: true, record: true };
  // An account whose age cannot be read is treated as established. The unknown
  // case has to fall on the side that records less.
  if (!userCreatedAt || Number.isNaN(userCreatedAt.getTime())) return { init: true, record: false };
  return { init: true, record: now.getTime() - userCreatedAt.getTime() < NEW_ACCOUNT_REPLAY_DAYS * DAY_MS };
}

/**
 * Query parameters that carry a credential or a person, and so never belong in
 * a third party's event store. `code` is the big one: invite links are
 * /signup?code=<registrationCode>, a capability token for an account that has
 * not set a password yet.
 */
export const SENSITIVE_QUERY_PARAMS = ['code', 'token', 'email', 'firstName', 'lastName', 'phoneNumber', 'phone', 'login', 'password'];

/**
 * Redacts the sensitive parameters of a URL while keeping the parameter names,
 * so "how many arrivals carried a code" stays answerable. A string that does
 * not parse as a URL passes through untouched.
 */
export function scrubSensitiveSearch(url: string): string {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return url;
  }
  let changed = false;
  for (const param of SENSITIVE_QUERY_PARAMS) {
    if (parsed.searchParams.has(param)) {
      parsed.searchParams.set(param, 'redacted');
      changed = true;
    }
  }
  return changed ? parsed.toString() : url;
}

const isUrlLike = (value: unknown): value is string => typeof value === 'string' && /^https?:\/\//.test(value) && value.includes('?');

/**
 * Runs on every outgoing event. Pageviews, autocapture and session metadata
 * all carry the page URL ($current_url, $referrer, $session_entry_url, the
 * hrefs autocapture reads off clicked elements) — every URL-shaped value gets
 * the same scrub, wherever the SDK decides to put one.
 */
export function scrubEventUrls(event: CaptureResult | null): CaptureResult | null {
  if (!event?.properties) return event;
  for (const [key, value] of Object.entries(event.properties)) {
    if (isUrlLike(value)) event.properties[key] = scrubSensitiveSearch(value);
  }
  const elements = event.properties.$elements;
  if (Array.isArray(elements)) {
    for (const element of elements) {
      if (element && typeof element === 'object' && isUrlLike((element as Record<string, unknown>).attr__href)) {
        (element as Record<string, unknown>).attr__href = scrubSensitiveSearch((element as { attr__href: string }).attr__href);
      }
    }
  }
  return event;
}

/**
 * Fragments of a message that means the request never reached the server. The
 * axios-shaped rejections errorReporting.ts sees carry a code it can read;
 * these are the same failures arriving as autocaptured uncaught errors, where
 * the message is all there is. Lowercased.
 *
 * The last fragment is a connection of another kind: a web view that has sat in
 * the background drops its IndexedDB connection, and the browser's own store
 * polls then reject with nothing on the app side to catch them.
 */
const CONNECTIVITY_MESSAGES = ['network error', 'failed to fetch', 'load failed', 'network connection was lost', 'no status code', 'connection to indexed database server lost'];

/** Every exception in the event, as "Type: message" — the shape the message matchers read. */
function exceptionSummaries(event: CaptureResult): string[] {
  const list = event.properties?.$exception_list;
  if (!Array.isArray(list)) return [];
  return list.map((exception) => {
    const { type, value } = (exception ?? {}) as { type?: unknown; value?: unknown };
    return `${typeof type === 'string' ? type : ''}: ${typeof value === 'string' ? value : ''}`;
  });
}

/**
 * Drops the exceptions there is nothing to fix in the app about, before they
 * are sent and before they are billed.
 *
 * A stale chunk is an out-of-date tab, not a crash — chunkReload.ts reloads it
 * and the fresh page works. A request that never reached the server is a phone
 * in a dead spot. Both used to be the top entries of the issue list, which is
 * what buries the crashes that are real.
 *
 * Third-party noise (the Outlook SafeLinks scanner, reCAPTCHA timeouts,
 * ResizeObserver, wallet extensions) is dropped by PostHog suppression rules
 * instead: those match on type and message server-side, so they need no
 * deploy to tune.
 */
export function dropNoisyExceptions(event: CaptureResult | null): CaptureResult | null {
  if (event?.event !== '$exception') return event;
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return null;
  const summaries = exceptionSummaries(event);
  if (summaries.some(isChunkLoadError)) return null;
  if (summaries.some((summary) => CONNECTIVITY_MESSAGES.some((fragment) => summary.toLowerCase().includes(fragment)))) return null;
  return event;
}

/**
 * Pings the support widget fires on every page load to say its code ran. Each
 * one is a billed event against the free plan's monthly quota, and none of
 * them is something a person did. The widget itself stays a switch in the
 * PostHog project settings, and what people do in it (opening it, sending a
 * message) is still sent.
 */
const PAGE_LOAD_PINGS = new Set(['$conversations_loaded', '$conversations_widget_loaded']);

export function dropPageLoadPings(event: CaptureResult | null): CaptureResult | null {
  return event && PAGE_LOAD_PINGS.has(event.event) ? null : event;
}

/** Runs on every outgoing event: the noise filters first, so a dropped event is never walked. */
export function beforeSendEvent(event: CaptureResult | null): CaptureResult | null {
  return scrubEventUrls(dropNoisyExceptions(dropPageLoadPings(event)));
}

interface PostHogSettings {
  api_key?: string;
  /** Ingestion host per brand (maligue / league2go): a first-party domain reverse-proxied to PostHog. */
  host?: Partial<Record<string, string>>;
  replay_all?: boolean;
}

/** The brand this page was served under, as the layout publishes it. */
function readBranding(): string | undefined {
  return (window as unknown as { appBranding?: string }).appBranding;
}

interface LoggedUserGlobal {
  id: number | string;
  createdAt?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
}

function readSettings(): PostHogSettings {
  return (window as unknown as { viewConfig?: { posthog?: PostHogSettings } }).viewConfig?.posthog ?? {};
}

/** The commit the served bundles were built from, published by the layout on the same channel as the settings above. */
function readRelease(): string | undefined {
  return (window as unknown as { viewConfig?: { build?: { git_hash?: string } } }).viewConfig?.build?.git_hash || undefined;
}

function readLoggedUser(): LoggedUserGlobal | null {
  const user = (window as unknown as { loggedUser?: LoggedUserGlobal | null }).loggedUser;
  return user?.id != null ? user : null;
}

/**
 * The server's signature over this user's distinct id (see
 * api/utils/posthogIdentity.ts). It is what lets PostHog's support widget tie
 * a ticket to the person instead of the browser it was opened in, so their
 * conversations follow them across devices and nobody else can claim them.
 * Absent until the signing token is provisioned, and PostHog then falls back
 * to its browser-session access control.
 */
function readIdentityHash(): string | undefined {
  return (window as unknown as { posthogIdentityHash?: string | null }).posthogIdentityHash || undefined;
}

/** Serialized through JSON on its way into the page, so the date arrives as a string. */
function parseCreatedAt(value: string | undefined): Date | null {
  return value ? new Date(value) : null;
}

/** Where this browser keeps a digest of the person properties it last sent. */
const SENT_PERSON_KEY = 'maligue_posthog_person';

/** A digest rather than the values, so the next person on a shared device does not find someone's email in storage. */
function digest(value: string): string {
  let hash = 5381;
  for (let i = 0; i < value.length; i++) hash = ((hash << 5) + hash + value.charCodeAt(i)) | 0;
  return (hash >>> 0).toString(36);
}

/** Storage can be missing or throw (private mode, blocked site data); the properties are then sent again, which is all it costs. */
function readSentPerson(): string | null {
  try {
    return window.localStorage.getItem(SENT_PERSON_KEY);
  } catch {
    return null;
  }
}

function rememberSentPerson(value: string): void {
  try {
    window.localStorage.setItem(SENT_PERSON_KEY, value);
  } catch {
    // Same as a failed read: the next load sends the properties again.
  }
}

/**
 * Identifies the signed-in user, with the email and name sent only when they
 * differ from what this browser last sent. The SDK's own duplicate check lives
 * in memory and starts empty on every page load, so passing them each time
 * bills one `$set` event per signed-in page load for the same two values.
 *
 * The id goes out on every load: it is what ties the session to the person,
 * and repeating it sends nothing once the browser is identified.
 */
function identifyPerson(posthog: PostHog, user: LoggedUserGlobal): void {
  const distinctId = String(user.id);
  const properties = { email: user.email, name: [user.firstName, user.lastName].filter(Boolean).join(' ') || undefined };
  const fingerprint = digest(JSON.stringify([distinctId, properties.email ?? null, properties.name ?? null]));
  // A browser still on another id (anonymous, or the previous person on this
  // device) is identifying right now, and the properties ride on that
  // $identify whatever the storage holds.
  const alreadySent = posthog.get_distinct_id() === distinctId && readSentPerson() === fingerprint;
  posthog.identify(distinctId, alreadySent ? undefined : properties);
  if (!alreadySent) rememberSentPerson(fingerprint);
}

/**
 * The in-flight (then settled) SDK. Doubles as the "are we on?" flag: while it
 * is null every helper below is a no-op, which is the whole no-key contract.
 */
let loading: Promise<PostHog | null> | null = null;

/** 'armed' — the gate granted a replay but the entry point is waiting for an engagement signal; 'requested' — the signal came in, possibly before the SDK (or even init) did. */
let deferredRecording: 'armed' | 'requested' | null = null;

export interface InitAnalyticsOptions {
  /**
   * Hold the granted replay until startDeferredRecording() is called. The
   * landing uses this so the replay quota goes to visitors who showed intent —
   * a bounce that never scrolls or navigates is never recorded.
   */
  deferRecording?: boolean;
}

/**
 * Starts PostHog for this page load. Called once per entry point; safe to call
 * again (later calls are ignored) and safe to reach during a server render,
 * where it does nothing.
 */
export function initAnalytics(options?: InitAnalyticsOptions): void {
  if (typeof window === 'undefined' || loading) return;

  const { api_key: apiKey, host: hosts, replay_all: replayAll } = readSettings();
  const branding = readBranding();
  const host = (branding && hosts?.[branding]) || undefined;
  const loggedUser = readLoggedUser();
  const gate = resolveAnalyticsGate({ hasKey: !!apiKey, replayAll: !!replayAll, loggedUser, userCreatedAt: parseCreatedAt(loggedUser?.createdAt), now: new Date() });
  if (!gate.init) return;

  // 'requested' survives from an engagement signal that beat init; anything
  // else holds the granted replay until that signal arrives.
  if (gate.record && options?.deferRecording && deferredRecording !== 'requested') deferredRecording = 'armed';

  // The signature is over the same string identify() is called with below, so
  // the verified id and the analytics id can never drift apart.
  const identityHash = readIdentityHash();
  const verifiedIdentity = loggedUser && identityHash ? { identity_distinct_id: String(loggedUser.id), identity_hash: identityHash } : undefined;

  // Imported here rather than at the top of the module: the SDK is ~200KB the
  // SSR bundle must never contain and the initial chunk should not carry for a
  // visitor the key was never configured for.
  loading = import('posthog-js')
    .then(({ default: posthog }) => {
      posthog.init(apiKey!, {
        api_host: host || DEFAULT_HOST,
        // Set at init rather than through setIdentity() after load: the layout
        // already knows who this is, so the widget is never briefly anonymous.
        ...verifiedIdentity,
        // Behind a first-party proxy the SDK cannot infer where the PostHog
        // app itself lives; toolbar and "view in PostHog" links need it.
        ui_host: 'https://us.posthog.com',
        defaults: '2025-05-24',
        // The free plan's 1M events a month is the budget, and every click
        // (autocapture, which rage clicks ride on), every page-speed sample
        // (web vitals) and every dead click or swipe would each spend a billed
        // event on data no report here reads. The funnel runs on pageviews and
        // the AnalyticsEvent names, error tracking on exceptions, so those are
        // what gets sent.
        autocapture: false,
        // Web vitals only: network timing stays with the project's replay
        // settings, so a recording still shows its requests.
        capture_performance: { web_vitals: false },
        capture_dead_clicks: false,
        // Each pageview already carries how long the previous page was open
        // and how far it was scrolled; a page leave would add one more billed
        // event per visit for the last page's share of that.
        capture_pageleave: false,
        before_send: beforeSendEvent,
        // Uncaught errors and unhandled rejections are the whole point of the
        // error tracking; left unset, whether they are captured is a project
        // setting in PostHog rather than something this code can be read for.
        capture_exceptions: true,
        // Off at init and turned on below only for the sessions the gate picks —
        // starting it here would record everyone for the moment it takes to decide.
        disable_session_recording: true,
        // A recording without the console is a picture without the caption:
        // the one React crash on record came with no component stack because
        // nothing kept what the browser printed. Set here rather than left to
        // the project setting, so a replay carries its console on every
        // deploy regardless of what the dashboard says.
        enable_recording_console_log: true,
        session_recording: {
          // The replay player's URL bar and captured requests come through
          // here, not before_send.
          maskCapturedNetworkRequestFn: (request) => ({ ...request, name: scrubSensitiveSearch(request.name) }),
          // The replay is meant to be read in full, signup form included: the
          // audience is the league operators themselves, and what a new manager
          // types into the signup form is the very thing the recording is for.
          // Passwords are the one input that stays masked.
          maskAllInputs: false,
          maskInputOptions: { password: true },
          maskTextSelector: null,
        },
      });
      // Which deploy an exception came from — the question a stack trace alone
      // cannot answer. A super property rather than a CI-uploaded release, so
      // the issue list can group and filter by it without a build step.
      const release = readRelease();
      if (release) posthog.register({ release });
      // The id joins the session to the database; the email and name are what
      // the PostHog people list shows, so a replay is found by who, not by id.
      if (loggedUser) identifyPerson(posthog, loggedUser);
      if (gate.record) {
        // The engagement signal may already have arrived while the SDK chunk
        // was downloading; recording then starts here rather than being lost.
        if (deferredRecording === 'armed') return posthog;
        posthog.startSessionRecording();
      }
      return posthog;
    })
    .catch(() => null);
}

/**
 * Releases a replay held by deferRecording. Idempotent; a no-op when nothing
 * was held (no key, gate said events-only, or recording already started). Safe
 * to call before initAnalytics — the request is kept and honoured at init.
 */
export function startDeferredRecording(): void {
  if (deferredRecording === 'requested') return;
  const wasArmed = deferredRecording === 'armed';
  deferredRecording = 'requested';
  if (wasArmed && loading) void loading.then((posthog) => posthog?.startSessionRecording());
}

/**
 * Hands the settled SDK to `use`, or does nothing at all — no key, a blocked
 * request, a failed init. The one door other modules reach PostHog through, so
 * the ~200KB chunk stays behind initAnalytics' dynamic import. Resolves once
 * `use` has run; callers that only fire and forget can ignore the promise.
 */
export function withAnalytics(use: (posthog: PostHog) => void): Promise<void> {
  if (!loading) return Promise.resolve();
  return loading.then((posthog) => {
    if (posthog) use(posthog);
  });
}

export interface CaptureEventOptions {
  /**
   * The caller is about to navigate away. Sends through sendBeacon and skips the
   * batching queue, which is what lets the event outlive the page — the default
   * transport would still be sitting in a queue when the document is torn down.
   */
  leavingPage?: boolean;
}

/**
 * Records one funnel event. Events fired before the SDK chunk lands are queued
 * on the same promise, so the first steps of a journey are not the ones lost.
 */
export function captureEvent(event: AnalyticsEvent, properties?: AnalyticsProperties, options?: CaptureEventOptions): void {
  if (!loading) return;
  // Error reporting funnels through here too, and a reporter that crashes the
  // page it is reporting on is worse than a lost event — so a capture that
  // throws stays inside this call instead of surfacing as a rejection.
  void loading.then((posthog) => posthog?.capture(event, properties, options?.leavingPage ? { transport: 'sendBeacon', send_instantly: true } : undefined)).catch(() => undefined);
}

/**
 * Forgets the identified person before the account leaves this browser
 * (logout, account deletion). The SDK persists the distinct id across page
 * loads, so without this the next login on a shared device — a family
 * tablet, a manager opening a player's account — would be refused as a merge
 * of two identified people and its sessions would stay on the previous
 * person. Never called on the signup → first authenticated load: that
 * anonymous-to-identified link is the funnel. Resolves once the reset is
 * done; a no-op when analytics never started.
 */
export async function resetAnalytics(): Promise<void> {
  if (!loading) return;
  const posthog = await loading;
  // The verified identity is a second, separate persistence from the distinct
  // id reset() clears: left behind, the next person on this device would open
  // the previous one's support tickets.
  posthog?.clearIdentity();
  posthog?.reset();
}

/** Test seam: drops the SDK so each case starts from an uninitialized module. */
export function resetAnalyticsForTest(): void {
  loading = null;
  deferredRecording = null;
}
